import React, { useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftjsToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Button, DropDown, Input, Title } from "@src/components";
import { serveDurationTypeDropDown } from "@src/constants/Commodity";
import { checkProductCodeDuplicated, useCreateLecture } from "./service";
import {
    InsertLectureClassRequestDto,
    InsertLectureRequestDto,
    ServeDurationType,
    WeekDay,
    WEEK_DAY_DROPDOWN,
} from "./service/types";

interface ScheduleForm {
    weekDay: WeekDay | "";
    startTime: string;
    endTime: string;
}

interface ClassForm {
    // 상품 정보
    code: string;
    isCodeChecked: boolean; // 상품 코드 중복검사 통과 여부
    price: string;
    priceIncludedVat: string;
    productName: string;
    serveDuration: string;
    serveDurationType: string;
    displayDescription: string;
    editorState: EditorState;
    // 클래스 정보
    title: string;
    instructor: string;
    instructorUserNo: string;
    location: string;
    capacity: string;
    sessionCount: string;
    recommendTarget: string;
    startDate: string;
    endDate: string;
    benefitStartAt: string;
    benefitEndAt: string;
    schedules: ScheduleForm[];
}

const initSchedule: ScheduleForm = {
    weekDay: "",
    startTime: "",
    endTime: "",
};

const createInitClass = (): ClassForm => ({
    code: "",
    isCodeChecked: false,
    price: "",
    priceIncludedVat: "",
    productName: "",
    serveDuration: "",
    serveDurationType: "",
    displayDescription: "",
    editorState: EditorState.createEmpty(),
    title: "",
    instructor: "",
    instructorUserNo: "",
    location: "",
    capacity: "",
    sessionCount: "",
    recommendTarget: "",
    startDate: "",
    endDate: "",
    benefitStartAt: "",
    benefitEndAt: "",
    schedules: [initSchedule],
});

const toTimeFormat = (time: string) => {
    // input[type=time] 값(HH:MM)을 HH:MM:SS로 변환
    return time.length === 5 ? `${time}:00` : time;
};

const toDateTimeFormat = (value: string) => {
    // input[type=datetime-local] 값(YYYY-MM-DDTHH:MM)을 YYYY-MM-DD HH:MM:SS로 변환
    const replaced = value.replace("T", " ");
    return replaced.length === 16 ? `${replaced}:00` : replaced;
};

const LecturesCreate = () => {
    const navigate = useNavigate();
    const createLecture = useCreateLecture();

    const [title, setTitle] = useState<string>("");
    const [contents, setContents] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [showStartAt, setShowStartAt] = useState<string>("");
    const [showEndAt, setShowEndAt] = useState<string>("");
    const [classes, setClasses] = useState<ClassForm[]>([createInitClass()]);
    const [errMessage, setErrMessage] = useState<string>("");

    const updateClass = (classIndex: number, name: keyof ClassForm, value: string) => {
        setClasses((prev) => prev.map((item, index) => (index === classIndex ? { ...item, [name]: value } : item)));
    };

    // 상품 코드 변경 시 중복검사 상태 초기화
    const updateClassCode = (classIndex: number, value: string) => {
        setClasses((prev) =>
            prev.map((item, index) => (index === classIndex ? { ...item, code: value, isCodeChecked: false } : item)),
        );
    };

    // 상품 코드 중복검사 (CommodityCreateContents 참고)
    const regTypeEngNum = /^[a-zA-Z0-9]+$/;

    const handleCheckDuplicate = async (classIndex: number) => {
        const code = classes[classIndex].code;
        if (!code) {
            return;
        }
        if (!regTypeEngNum.test(code)) {
            alert("영어, 숫자만 입력해주세요");
            return;
        }
        const hasSameCodeInForm = classes.some((item, index) => index !== classIndex && item.code === code);
        if (hasSameCodeInForm) {
            alert("다른 클래스에서 사용 중인 상품코드입니다.");
            return;
        }

        const isDuplicated = await checkProductCodeDuplicated(code);
        setClasses((prev) =>
            prev.map((item, index) => (index === classIndex ? { ...item, isCodeChecked: !isDuplicated } : item)),
        );
        isDuplicated ? alert("이미 존재하는 상품코드입니다.") : alert("사용가능한 상품코드입니다.");
    };

    // 노출용 설명 에디터 (CommodityCreateContents 참고)
    const updateClassEditor = (classIndex: number, state: EditorState) => {
        const rawData = convertToRaw(state.getCurrentContent());
        const isEmpty = rawData.blocks.length === 1 && rawData.blocks[0].text === "";
        const html = isEmpty ? "" : draftjsToHtml(rawData);

        setClasses((prev) =>
            prev.map((item, index) =>
                index === classIndex ? { ...item, editorState: state, displayDescription: html } : item,
            ),
        );
    };

    const updateSchedule = (classIndex: number, scheduleIndex: number, name: keyof ScheduleForm, value: string) => {
        setClasses((prev) =>
            prev.map((item, index) => {
                if (index !== classIndex) {
                    return item;
                }
                return {
                    ...item,
                    schedules: item.schedules.map((schedule, sIndex) =>
                        sIndex === scheduleIndex ? { ...schedule, [name]: value } : schedule,
                    ),
                };
            }),
        );
    };

    const addClass = () => {
        setClasses((prev) => [...prev, createInitClass()]);
    };

    const removeClass = (classIndex: number) => {
        setClasses((prev) => prev.filter((_, index) => index !== classIndex));
    };

    const addSchedule = (classIndex: number) => {
        setClasses((prev) =>
            prev.map((item, index) =>
                index === classIndex ? { ...item, schedules: [...item.schedules, initSchedule] } : item,
            ),
        );
    };

    const removeSchedule = (classIndex: number, scheduleIndex: number) => {
        setClasses((prev) =>
            prev.map((item, index) =>
                index === classIndex
                    ? {
                          ...item,
                          schedules: item.schedules.filter((_, sIndex) => sIndex !== scheduleIndex),
                      }
                    : item,
            ),
        );
    };

    const validate = (): string => {
        if (!title) return "강의 제목을 입력해주세요.";
        if (!contents) return "강의 요약 소개를 입력해주세요.";
        if (classes.length === 0) return "클래스를 1개 이상 등록해주세요.";

        for (let i = 0; i < classes.length; i += 1) {
            const lectureClass = classes[i];
            if (!lectureClass.title) {
                return `${i + 1}번째 클래스 이름을 입력해주세요.`;
            }
            if (!lectureClass.code) {
                return `${i + 1}번째 클래스의 상품 코드를 입력해주세요.`;
            }
            if (!lectureClass.isCodeChecked) {
                return `${i + 1}번째 클래스의 상품 코드 중복검사를 해주세요.`;
            }
            if (lectureClass.price === "") {
                return `${i + 1}번째 클래스의 판매 가격을 입력해주세요.`;
            }
            if (lectureClass.priceIncludedVat === "") {
                return `${i + 1}번째 클래스의 부가세를 입력해주세요.`;
            }
            if (lectureClass.schedules.length === 0) {
                return `${i + 1}번째 클래스의 강의 시간을 1개 이상 등록해주세요.`;
            }
            for (let j = 0; j < lectureClass.schedules.length; j += 1) {
                const schedule = lectureClass.schedules[j];
                if (!schedule.weekDay || !schedule.startTime || !schedule.endTime) {
                    return `${i + 1}번째 클래스의 ${j + 1}번째 강의 시간(요일/시작/종료)을 모두 입력해주세요.`;
                }
            }
        }
        return "";
    };

    const handleSubmit = () => {
        const message = validate();
        if (message) {
            setErrMessage(message);
            return;
        }
        setErrMessage("");

        const requestClasses: InsertLectureClassRequestDto[] = classes.map((lectureClass) => ({
            code: lectureClass.code,
            price: Number(lectureClass.price),
            priceIncludedVat: Number(lectureClass.priceIncludedVat),
            productName: lectureClass.productName || undefined,
            serveDuration: lectureClass.serveDuration ? Number(lectureClass.serveDuration) : undefined,
            serveDurationType: (lectureClass.serveDurationType as ServeDurationType) || undefined,
            displayDescription: lectureClass.displayDescription || undefined,
            title: lectureClass.title,
            instructor: lectureClass.instructor || undefined,
            instructorUserNo: lectureClass.instructorUserNo ? Number(lectureClass.instructorUserNo) : undefined,
            location: lectureClass.location || undefined,
            capacity: lectureClass.capacity ? Number(lectureClass.capacity) : undefined,
            sessionCount: lectureClass.sessionCount ? Number(lectureClass.sessionCount) : undefined,
            recommendTarget: lectureClass.recommendTarget || undefined,
            startDate: lectureClass.startDate || undefined,
            endDate: lectureClass.endDate || undefined,
            benefitStartAt: lectureClass.benefitStartAt ? toDateTimeFormat(lectureClass.benefitStartAt) : undefined,
            benefitEndAt: lectureClass.benefitEndAt ? toDateTimeFormat(lectureClass.benefitEndAt) : undefined,
            schedules: lectureClass.schedules.map((schedule) => ({
                weekDay: schedule.weekDay as WeekDay,
                startTime: toTimeFormat(schedule.startTime),
                endTime: toTimeFormat(schedule.endTime),
            })),
        }));

        const requestData: InsertLectureRequestDto = {
            title,
            contents,
            description: description || undefined,
            showStartAt: showStartAt || undefined,
            showEndAt: showEndAt || undefined,
            classes: requestClasses,
        };

        createLecture.mutate(requestData, {
            onSuccess: () => {
                navigate("/offline/lectures");
            },
            onError: () => {
                setErrMessage("강의 등록에 실패했습니다.");
            },
        });
    };

    return (
        <div css={createStyle}>
            <Title title="강의 등록" />
            <StyledContents>
                <Title title="강의 정보" size="small" />
                <StyledRow>
                    <text>
                        <span>*</span>강의 제목
                    </text>
                    <Input
                        placeholder="김병진 교수의 A급 우량경매 실전분석"
                        size="medium"
                        value={title}
                        handleChange={(e) => setTitle(e.target.value)}
                    />
                </StyledRow>
                <StyledRow>
                    <text>
                        <span>*</span>요약 소개
                    </text>
                    <Input
                        placeholder="경매 입문자를 위한 오프라인 강의"
                        size="big"
                        value={contents}
                        handleChange={(e) => setContents(e.target.value)}
                    />
                </StyledRow>
                <StyledRow>
                    <text>상세 설명</text>
                    <textarea
                        placeholder="강의 상세 설명을 입력해주세요"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </StyledRow>
                <StyledRow>
                    <text>노출 기간</text>
                    <input type="date" value={showStartAt} onChange={(e) => setShowStartAt(e.target.value)} />
                    <span className="tilde">~</span>
                    <input type="date" value={showEndAt} onChange={(e) => setShowEndAt(e.target.value)} />
                </StyledRow>

                <StyledClassHeader>
                    <Title title="클래스 정보" size="small" />
                    <Button color="purple" size="medium" label="클래스 추가" handleClick={addClass} />
                </StyledClassHeader>
                {classes.map((lectureClass, classIndex) => (
                    <StyledClassBox key={classIndex}>
                        <div className="classBoxHeader">
                            <span>클래스 {classIndex + 1}</span>
                            {classes.length > 1 && (
                                <Button
                                    color="gray"
                                    size="small"
                                    label="삭제"
                                    handleClick={() => removeClass(classIndex)}
                                />
                            )}
                        </div>
                        <StyledRow>
                            <text>
                                <span>*</span>클래스 이름
                            </text>
                            <Input
                                placeholder="주말반"
                                size="medium"
                                value={lectureClass.title}
                                handleChange={(e) => updateClass(classIndex, "title", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>강사명</text>
                            <Input
                                placeholder="홍길동"
                                size="medium"
                                value={lectureClass.instructor}
                                handleChange={(e) => updateClass(classIndex, "instructor", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>강사 회원번호</text>
                            <Input
                                placeholder="100"
                                type="number"
                                size="medium"
                                value={lectureClass.instructorUserNo}
                                handleChange={(e) => updateClass(classIndex, "instructorUserNo", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>강의 장소</text>
                            <Input
                                placeholder="서울 강남구 도곡동 955-14 지하 1층"
                                size="big"
                                value={lectureClass.location}
                                handleChange={(e) => updateClass(classIndex, "location", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>정원</text>
                            <Input
                                placeholder="20"
                                type="number"
                                size="small"
                                value={lectureClass.capacity}
                                handleChange={(e) => updateClass(classIndex, "capacity", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>총 강의 횟수</text>
                            <Input
                                placeholder="16"
                                type="number"
                                size="small"
                                value={lectureClass.sessionCount}
                                handleChange={(e) => updateClass(classIndex, "sessionCount", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>추천 대상</text>
                            <Input
                                placeholder="은퇴자·시니어 투자자 추천"
                                size="big"
                                value={lectureClass.recommendTarget}
                                handleChange={(e) => updateClass(classIndex, "recommendTarget", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>강의 기간</text>
                            <input
                                type="date"
                                value={lectureClass.startDate}
                                onChange={(e) => updateClass(classIndex, "startDate", e.target.value)}
                            />
                            <span className="tilde">~</span>
                            <input
                                type="date"
                                value={lectureClass.endDate}
                                onChange={(e) => updateClass(classIndex, "endDate", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>
                                <span>*</span>강의 시간
                            </text>
                            <div className="scheduleList">
                                {lectureClass.schedules.map((schedule, scheduleIndex) => (
                                    <div className="scheduleRow" key={scheduleIndex}>
                                        <DropDown
                                            data={WEEK_DAY_DROPDOWN}
                                            defaultValue={schedule.weekDay}
                                            placeholder="요일"
                                            handleChange={(e: any) =>
                                                updateSchedule(classIndex, scheduleIndex, "weekDay", e.target.value)
                                            }
                                        />
                                        <input
                                            type="time"
                                            value={schedule.startTime}
                                            onChange={(e) =>
                                                updateSchedule(classIndex, scheduleIndex, "startTime", e.target.value)
                                            }
                                        />
                                        <span className="tilde">~</span>
                                        <input
                                            type="time"
                                            value={schedule.endTime}
                                            onChange={(e) =>
                                                updateSchedule(classIndex, scheduleIndex, "endTime", e.target.value)
                                            }
                                        />
                                        {lectureClass.schedules.length > 1 && (
                                            <Button
                                                color="gray"
                                                size="xSmall"
                                                label="삭제"
                                                handleClick={() => removeSchedule(classIndex, scheduleIndex)}
                                            />
                                        )}
                                    </div>
                                ))}
                                <div>
                                    <Button
                                        color="white"
                                        size="small"
                                        label="시간 추가"
                                        handleClick={() => addSchedule(classIndex)}
                                    />
                                </div>
                            </div>
                        </StyledRow>

                        <StyledProductDivider>상품 정보</StyledProductDivider>
                        <StyledRow>
                            <text>
                                <span>*</span>상품 코드
                            </text>
                            <Input
                                placeholder="영문, 숫자만 사용하여 입력해주세요"
                                size="medium"
                                value={lectureClass.code}
                                handleChange={(e) => updateClassCode(classIndex, e.target.value)}
                            />
                            <div className="checkBtn">
                                <Button
                                    color="gd"
                                    size="small"
                                    label="중복검사"
                                    handleClick={() => handleCheckDuplicate(classIndex)}
                                />
                            </div>
                            {lectureClass.isCodeChecked && <span className="checkDone">확인 완료</span>}
                        </StyledRow>
                        <StyledRow>
                            <text>
                                <span>*</span>판매 가격
                            </text>
                            <Input
                                placeholder="판매 가격"
                                type="number"
                                size="medium"
                                value={lectureClass.price}
                                handleChange={(e) => {
                                    updateClass(classIndex, "price", e.target.value);
                                    const vat = Math.round(Number(e.target.value) * 0.1).toString();
                                    updateClass(classIndex, "priceIncludedVat", vat);
                                }}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>
                                <span>*</span>부가세
                            </text>
                            <Input
                                placeholder="판매 가격에 포함된 부가세 (자동입력)"
                                type="number"
                                size="medium"
                                isDisabled
                                value={lectureClass.priceIncludedVat}
                                handleChange={(e) => updateClass(classIndex, "priceIncludedVat", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>상품 이름</text>
                            <Input
                                placeholder={`미입력 시 "강의명(반명)"으로 자동 구성`}
                                size="big"
                                value={lectureClass.productName}
                                handleChange={(e) => updateClass(classIndex, "productName", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow>
                            <text>상품 제공 기간</text>
                            <Input
                                placeholder="0"
                                type="number"
                                size="small"
                                value={lectureClass.serveDuration}
                                handleChange={(e) => updateClass(classIndex, "serveDuration", e.target.value)}
                            />
                            <div className="durationType">
                                <DropDown
                                    data={serveDurationTypeDropDown}
                                    defaultValue={lectureClass.serveDurationType}
                                    placeholder="단위"
                                    handleChange={(e: any) =>
                                        updateClass(classIndex, "serveDurationType", e.target.value)
                                    }
                                />
                            </div>
                        </StyledRow>
                        <StyledRow>
                            <text>혜택 적용 기간</text>
                            <input
                                type="datetime-local"
                                value={lectureClass.benefitStartAt}
                                onChange={(e) => updateClass(classIndex, "benefitStartAt", e.target.value)}
                            />
                            <span className="tilde">~</span>
                            <input
                                type="datetime-local"
                                value={lectureClass.benefitEndAt}
                                onChange={(e) => updateClass(classIndex, "benefitEndAt", e.target.value)}
                            />
                        </StyledRow>
                        <StyledRow className="editorRow">
                            <text>
                                상품설명
                                <br />
                                (노출용)
                            </text>
                            <div className="editorWrap">
                                <Editor
                                    placeholder="상품 노출용 설명을 입력해주세요"
                                    editorState={lectureClass.editorState}
                                    onEditorStateChange={(state: EditorState) => updateClassEditor(classIndex, state)}
                                    localization={{ locale: "ko" }}
                                    editorStyle={{
                                        height: "300px",
                                        width: "100%",
                                        border: "3px solid lightgray",
                                        padding: "20px",
                                        backgroundColor: "white",
                                    }}
                                />
                            </div>
                        </StyledRow>
                    </StyledClassBox>
                ))}

                {errMessage && <StyledError>{errMessage}</StyledError>}
                <StyledSubmitBox>
                    <Button
                        color="gray"
                        size="medium"
                        label="취소"
                        handleClick={() => {
                            navigate("/offline/lectures");
                        }}
                    />
                    <Button
                        color="purple"
                        size="medium"
                        label="등록"
                        isDisabled={createLecture.isLoading}
                        handleClick={handleSubmit}
                    />
                </StyledSubmitBox>
            </StyledContents>
        </div>
    );
};

export default LecturesCreate;

const createStyle = css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 40px;
    position: relative;
`;

const StyledContents = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 54px;
    width: 100%;

    input[type="date"],
    input[type="time"],
    input[type="datetime-local"] {
        padding: 6px 10px;
        border: 1.5px solid #e3e3e6;
        border-radius: 8px;
        outline: none;
        font-size: 14px;
        color: #222222;
    }
`;

const StyledRow = styled.div`
    display: flex;
    margin-top: 17px;
    align-items: center;
    font-weight: 400;
    font-size: 14px;
    color: #222222;

    & > text {
        width: 110px;
        flex-shrink: 0;
        & > span {
            color: #f45b5b;
        }
    }
    & > textarea {
        width: 500px;
        height: 100px;
        padding: 10px;
        border: 1.5px solid #e3e3e6;
        border-radius: 8px;
        outline: none;
        font-size: 14px;
        resize: vertical;
    }
    & .tilde {
        margin: 0 8px;
    }
    & > .durationType {
        margin-left: 8px;
    }
    & > .checkBtn {
        margin-left: 8px;
    }
    & > .checkDone {
        margin-left: 8px;
        color: #634afd;
        font-size: 13px;
    }
    & > .scheduleList {
        display: flex;
        flex-direction: column;
        gap: 8px;

        & > .scheduleRow {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }
    &.editorRow {
        align-items: flex-start;

        & > .editorWrap {
            width: 100%;
        }
    }
`;

const StyledClassHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 45px;
`;

const StyledClassBox = styled.div`
    margin-top: 20px;
    padding: 24px;
    background-color: #f4f5f7;
    border-radius: 8px;

    & > .classBoxHeader {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 700;
        font-size: 15px;
        color: #222222;
    }
`;

const StyledProductDivider = styled.div`
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #e3e3e6;
    font-weight: 700;
    font-size: 15px;
    color: #222222;
`;

const StyledError = styled.div`
    margin-top: 20px;
    color: #f45b5b;
    font-size: 14px;
    text-align: right;
`;

const StyledSubmitBox = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 30px;
    padding-bottom: 40px;
`;
